import React, { useState, useEffect } from 'react';
import { 
  Settings as SettingsIcon, 
  CreditCard, 
  Globe, 
  Settings as SystemIcon,
  Save,
  RefreshCw,
  TestTube,
  Shield,
  AlertTriangle
} from 'lucide-react';
import { configService } from '../services/configService';
import { CheckoutConfig, ApiConfig, SystemConfig } from '../interfaces/Config';
import { DefaultLoader } from '../components';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'checkout' | 'api' | 'system'>('checkout');
  const [checkoutConfig, setCheckoutConfig] = useState<CheckoutConfig | null>(null);
  const [apiConfig, setApiConfig] = useState<ApiConfig | null>(null);
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const [checkout, apiConf, system] = await Promise.all([
        configService.getCheckoutConfig(),
        configService.getApiConfig(),
        configService.getSystemConfig()
      ]);
      setCheckoutConfig(checkout);
      setApiConfig(apiConf);
      setSystemConfig(system);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
      setError('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCheckout = async () => {
    if (!checkoutConfig) return;
    
    try {
      setSaving(true);
      const updated = await configService.updateCheckoutConfig(checkoutConfig);
      setCheckoutConfig(updated);
      setSuccess('Configurações de pagamento salvas com sucesso!');
      setError(null);
    } catch (err) {
      console.error('Erro ao salvar configurações:', err);
      setError('Erro ao salvar configurações de pagamento');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveApi = async () => {
    if (!apiConfig) return;
    
    try {
      setSaving(true);
      const updated = await configService.updateApiConfig(apiConfig);
      setApiConfig(updated);
      setSuccess('Configurações de API salvas com sucesso!');
      setError(null);
    } catch (err) {
      console.error('Erro ao salvar configurações:', err);
      setError('Erro ao salvar configurações de API');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSystem = async () => {
    if (!systemConfig) return;
    
    try {
      setSaving(true);
      const updated = await configService.updateSystemConfig(systemConfig);
      setSystemConfig(updated);
      setSuccess('Configurações do sistema salvas com sucesso!');
      setError(null);
    } catch (err) {
      console.error('Erro ao salvar configurações:', err);
      setError('Erro ao salvar configurações do sistema');
    } finally {
      setSaving(false);
    }
  };

  const handleTestApi = async () => {
    if (!apiConfig?.flights_api_url) return;
    
    try {
      setTesting(true);
      const result = await configService.testApiConnection(apiConfig.flights_api_url);
      if (result.success) {
        setSuccess(`Conexão testada com sucesso: ${result.message}`);
        setError(null);
      } else {
        setError(`Erro no teste de conexão: ${result.message}`);
        setSuccess(null);
      }
    } catch (err) {
      console.error('Erro ao testar API:', err);
      setError('Erro ao testar conexão com a API');
    } finally {
      setTesting(false);
    }
  };

  const handleResetConfig = async (type: 'checkout' | 'api' | 'system') => {
    if (!window.confirm(`Tem certeza que deseja resetar as configurações de ${type}? Esta ação não pode ser desfeita.`)) {
      return;
    }
    
    try {
      setSaving(true);
      await configService.resetConfig(type);
      await loadConfigurations();
      setSuccess(`Configurações de ${type} resetadas com sucesso!`);
      setError(null);
    } catch (err) {
      console.error('Erro ao resetar configurações:', err);
      setError(`Erro ao resetar configurações de ${type}`);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: 'checkout', label: 'Pagamentos', icon: <CreditCard size={20} /> },
    { key: 'api', label: 'APIs', icon: <Globe size={20} /> },
    { key: 'system', label: 'Sistema', icon: <SystemIcon size={20} /> }
  ];

  if (loading) {
    return <DefaultLoader title="Configurações" messages={['Carregando configurações...', 'Processando dados do sistema...', 'Quase pronto...']} />;
  }

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ 
          fontSize: 'var(--font-size-24)', 
          fontWeight: 600, 
          color: 'var(--color-dimgray)', 
          margin: '0 0 8px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <SettingsIcon size={24} />
          Configurações do Sistema
        </h2>
        <p style={{ 
          fontSize: 'var(--font-size-16)', 
          color: 'var(--color-darkgray)', 
          margin: 0 
        }}>
          Gerencie as configurações globais do sistema.
        </p>
      </div>

      {error && (
        <div className="card" style={{
          backgroundColor: '#fee2e2',
          borderColor: '#fecaca',
          color: '#dc2626',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={20} />
            {error}
          </div>
        </div>
      )}

      {success && (
        <div className="card" style={{
          backgroundColor: '#dcfce7',
          borderColor: '#bbf7d0',
          color: '#16a34a',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Shield size={20} />
            {success}
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid var(--color-gray)'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              style={{
                padding: '16px 24px',
                border: 'none',
                backgroundColor: activeTab === tab.key ? 'var(--color-primary)' : 'transparent',
                color: activeTab === tab.key ? 'white' : 'var(--color-dimgray)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: 'var(--font-size-14)',
                fontWeight: 500
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: '24px' }}>
          {activeTab === 'checkout' && checkoutConfig && (
            <div>
              <h3 style={{ marginBottom: '20px', color: 'var(--color-dimgray)' }}>
                Configurações de Pagamento
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                marginBottom: '24px'
              }}>
                <div className="card" style={{ backgroundColor: 'var(--color-lightgray)' }}>
                  <h4 style={{ marginBottom: '16px' }}>Métodos de Pagamento</h4>
                  
                  {[
                    { key: 'pix_enabled', label: 'PIX' },
                    { key: 'credit_card_enabled', label: 'Cartão de Crédito' },
                    { key: 'debit_card_enabled', label: 'Cartão de Débito' },
                    { key: 'kairu_enabled', label: 'Kairu' },
                    { key: 'zartoes_enabled', label: 'Zartões' },
                    { key: 'transfer_enabled', label: 'Transferência' }
                  ].map(method => (
                    <label key={method.key} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={checkoutConfig[method.key as keyof CheckoutConfig] as boolean}
                        onChange={(e) => setCheckoutConfig({
                          ...checkoutConfig,
                          [method.key]: e.target.checked
                        })}
                      />
                      {method.label}
                    </label>
                  ))}
                </div>

                <div className="card" style={{ backgroundColor: 'var(--color-lightgray)' }}>
                  <h4 style={{ marginBottom: '16px' }}>Recursos Adicionais</h4>
                  
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={checkoutConfig.insurance_enabled}
                      onChange={(e) => setCheckoutConfig({
                        ...checkoutConfig,
                        insurance_enabled: e.target.checked
                      })}
                    />
                    Seguro Habilitado
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={checkoutConfig.enable_nota_fiscal}
                      onChange={(e) => setCheckoutConfig({
                        ...checkoutConfig,
                        enable_nota_fiscal: e.target.checked
                      })}
                    />
                    Nota Fiscal Habilitada
                  </label>
                </div>

                <div className="card" style={{ backgroundColor: 'var(--color-lightgray)' }}>
                  <h4 style={{ marginBottom: '16px' }}>Notificações</h4>

                  {[
                    { key: 'email_enabled', label: 'E-mail' },
                    { key: 'sms_enabled', label: 'SMS' },
                    { key: 'whatsapp_enabled', label: 'WhatsApp' },
                  ].map(item => (
                    <label key={item.key} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={checkoutConfig[item.key as keyof CheckoutConfig] as boolean}
                        onChange={(e) => setCheckoutConfig({
                          ...checkoutConfig,
                          [item.key]: e.target.checked
                        })}
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveCheckout}
                  disabled={saving}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {saving ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                  {saving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'api' && apiConfig && (
            <div>
              <h3 style={{ marginBottom: '20px', color: 'var(--color-dimgray)' }}>
                Configurações de API
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '20px',
                marginBottom: '24px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--font-size-14)',
                    fontWeight: 500,
                    color: 'var(--color-dimgray)',
                    marginBottom: '8px'
                  }}>
                    URL da API de Voos
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="url"
                      value={apiConfig.flights_api_url}
                      onChange={(e) => setApiConfig({
                        ...apiConfig,
                        flights_api_url: e.target.value
                      })}
                      className="input"
                      style={{ flex: 1 }}
                      placeholder="https://api.flights.com"
                    />
                    <button
                      className="btn btn-secondary"
                      onClick={handleTestApi}
                      disabled={testing}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      {testing ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <TestTube size={16} />}
                      Testar
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--font-size-14)',
                      fontWeight: 500,
                      color: 'var(--color-dimgray)',
                      marginBottom: '8px'
                    }}>
                      Timeout (ms)
                    </label>
                    <input
                      type="number"
                      value={apiConfig.api_timeout}
                      onChange={(e) => setApiConfig({
                        ...apiConfig,
                        api_timeout: parseInt(e.target.value)
                      })}
                      className="input"
                      min="1000"
                      max="120000"
                    />
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--font-size-14)',
                      fontWeight: 500,
                      color: 'var(--color-dimgray)',
                      marginBottom: '8px'
                    }}>
                      Tentativas de Retry
                    </label>
                    <input
                      type="number"
                      value={apiConfig.retry_attempts}
                      onChange={(e) => setApiConfig({
                        ...apiConfig,
                        retry_attempts: parseInt(e.target.value)
                      })}
                      className="input"
                      min="0"
                      max="10"
                    />
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--font-size-14)',
                      fontWeight: 500,
                      color: 'var(--color-dimgray)',
                      marginBottom: '8px'
                    }}>
                      Delay entre Retries (ms)
                    </label>
                    <input
                      type="number"
                      value={apiConfig.retry_delay}
                      onChange={(e) => setApiConfig({
                        ...apiConfig,
                        retry_delay: parseInt(e.target.value)
                      })}
                      className="input"
                      min="100"
                      max="30000"
                    />
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--font-size-14)',
                      fontWeight: 500,
                      color: 'var(--color-dimgray)',
                      marginBottom: '8px'
                    }}>
                      Rate Limit (req/min)
                    </label>
                    <input
                      type="number"
                      value={apiConfig.rate_limit}
                      onChange={(e) => setApiConfig({
                        ...apiConfig,
                        rate_limit: parseInt(e.target.value)
                      })}
                      className="input"
                      min="1"
                      max="1000"
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveApi}
                  disabled={saving}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {saving ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                  {saving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
                
                <button
                  className="btn btn-secondary"
                  onClick={() => handleResetConfig('api')}
                  disabled={saving}
                >
                  Resetar Padrões
                </button>
              </div>
            </div>
          )}

          {activeTab === 'system' && systemConfig && (
            <div>
              <h3 style={{ marginBottom: '20px', color: 'var(--color-dimgray)' }}>
                Configurações do Sistema
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px',
                marginBottom: '24px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--font-size-14)',
                    fontWeight: 500,
                    color: 'var(--color-dimgray)',
                    marginBottom: '8px'
                  }}>
                    Paginação Padrão
                  </label>
                  <input
                    type="number"
                    value={systemConfig.default_pagination}
                    onChange={(e) => setSystemConfig({
                      ...systemConfig,
                      default_pagination: parseInt(e.target.value)
                    })}
                    className="input"
                    min="5"
                    max="100"
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--font-size-14)',
                    fontWeight: 500,
                    color: 'var(--color-dimgray)',
                    marginBottom: '8px'
                  }}>
                    Max Resultados de Busca
                  </label>
                  <input
                    type="number"
                    value={systemConfig.max_search_results}
                    onChange={(e) => setSystemConfig({
                      ...systemConfig,
                      max_search_results: parseInt(e.target.value)
                    })}
                    className="input"
                    min="10"
                    max="1000"
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--font-size-14)',
                    fontWeight: 500,
                    color: 'var(--color-dimgray)',
                    marginBottom: '8px'
                  }}>
                    Expiração JWT (segundos)
                  </label>
                  <input
                    type="number"
                    value={systemConfig.jwt_expiration}
                    onChange={(e) => setSystemConfig({
                      ...systemConfig,
                      jwt_expiration: parseInt(e.target.value)
                    })}
                    className="input"
                    min="300"
                    max="86400"
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--font-size-14)',
                    fontWeight: 500,
                    color: 'var(--color-dimgray)',
                    marginBottom: '8px'
                  }}>
                    Timeout de Sessão (segundos)
                  </label>
                  <input
                    type="number"
                    value={systemConfig.session_timeout}
                    onChange={(e) => setSystemConfig({
                      ...systemConfig,
                      session_timeout: parseInt(e.target.value)
                    })}
                    className="input"
                    min="300"
                    max="28800"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '16px', color: 'var(--color-dimgray)' }}>
                  Modos do Sistema
                </h4>
                
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={systemConfig.maintenance_mode}
                    onChange={(e) => setSystemConfig({
                      ...systemConfig,
                      maintenance_mode: e.target.checked
                    })}
                  />
                  Modo de Manutenção
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={systemConfig.debug_mode}
                    onChange={(e) => setSystemConfig({
                      ...systemConfig,
                      debug_mode: e.target.checked
                    })}
                  />
                  Modo Debug
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveSystem}
                  disabled={saving}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {saving ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                  {saving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
                
                <button
                  className="btn btn-secondary"
                  onClick={() => handleResetConfig('system')}
                  disabled={saving}
                >
                  Resetar Padrões
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;